import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import useUpdateGroup from "@/apis/groups/updateGroup";
import type { IUpdateGroupRequest } from "@/apis/groups/updateGroup";
import useQueryGetGroup from "@/apis/groups/getGroup";
import GroupForm from "./components/GroupForm";
import type { ICreateGroupRequest } from "@/apis/groups/createGroup";

const GroupEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [serverError, setServerError] = useState<string | undefined>();

  if (!id) {
    navigate("/groups");
    return null;
  }

  // 그룹 정보 조회
  const {
    data: group,
    isLoading: isLoadingGroup,
    error: groupError,
  } = useQueryGetGroup(id);

  // 수정 API 훅
  const { mutate: updateGroup, isPending: isUpdating } = useUpdateGroup(id);

  // 폼 제출 핸들러
  const handleSubmit = (formData: ICreateGroupRequest) => {
    if (!group) return;

    // 업데이트 데이터 준비
    const updateData: IUpdateGroupRequest = {
      name: formData.name,
      status: formData.status,
    };

    // API 호출
    updateGroup(updateData, {
      onSuccess: () => {
        // 성공 시 상세 페이지로 이동
        navigate(`/groups/${id}`);
      },
      onError: (error: any) => {
        // 서버 오류 처리
        setServerError(error?.message || "Failed to update group");
      },
    });
  };

  // 상세 페이지로 돌아가기
  const handleCancel = () => {
    navigate(`/groups/${id}`);
  };

  // 로딩 중
  if (isLoadingGroup) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading group information...</Text>
        </div>
      </PageContainer>
    );
  }

  // 오류 발생
  if (groupError || !group) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load group information. Please try again.</Text>
          <Button onClick={() => navigate("/groups")} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        {serverError && (
          <div className="p-4 bg-red-50 text-red-600 rounded-md">
            {serverError}
          </div>
        )}

        <div className="flex items-center gap-4">
          <Button plain onClick={handleCancel}>
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </Button>

          <div>
            <Heading>Edit Group: {group.name}</Heading>
            <Text className="text-zinc-500">
              Update the basic information below to modify this group. Members
              can be managed from the group detail page.
            </Text>
          </div>
        </div>

        {/* 그룹 수정 폼 */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <GroupForm
            initialData={group}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isUpdating}
            serverError={serverError}
            isEditMode={true}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default GroupEditPage;
